// WHAT: Utility functions for mapping roadmap enums to display strings
// WHY:  Provides consistent string conversion for roadmap types and scores
// DOMAIN: Admin → Roadmap → Utils
// DEPENDENCIES: roadmap_types for enum types
// RISKS:
//   - NOTE: typeToString returns empty string for unknown types - may hide errors
//   - NOTE: scoreLevelToString returns empty string for undefined - may need better handling

import {
  type Roadmap_Item_Type,
  type Roadmap_Item_Score,
} from "@/domains/admin/roadmap/types/roadmap-types";

/**
 * WHAT:
 *   Converts roadmap item type enum to display string.
 *
 * WHY:
 *   Provides consistent string representation for roadmap types in UI.
 *
 * BEHAVIOR:
 *   - Returns type string for known types
 *   - Returns empty string for unknown types
 *
 * RISKS:
 *   - NOTE: Returns empty string for unknown types - may hide type errors
 *   - NOTE: Consider returning a default value or throwing error for unknown types
 *
 * @param type - The roadmap item type to convert
 *
 * @returns {string} Display string for the type
 *
 */
export const typeToString = (type: Roadmap_Item_Type): string => {
  switch (type) {
    case "bug":
    case "feature":
    case "insight":
    case "idea": {
      return type;
    }
    default: {
      return "";
    }
  }
};

/**
 * WHAT:
 *   Converts roadmap score level enum to display string.
 *
 * WHY:
 *   Provides consistent string representation for score levels in UI.
 *
 * BEHAVIOR:
 *   - Returns score string if provided
 *   - Returns empty string if undefined
 *
 * RISKS:
 *   - NOTE: Returns empty string for undefined - may need better default handling
 *   - NOTE: Consider returning 'N/A' or similar for undefined values
 *
 * @param level - The score level to convert (optional)
 *
 * @returns {string} Display string for the score level
 *
 */
export const scoreLevelToString = (level?: Roadmap_Item_Score): string => {
  return level ?? "";
};
